import { useState } from 'react';
import { isAbleToSeeUIElement } from '../../utils/roles/client-roles';

interface UseChangeLogGateOptions {
  permissionValue: string;
  changeLogRoles: string[];
  /** Override the initial open state. Defaults to `needsChangeLog`. */
  initialOpen?: boolean;
  /** Initial message value. Used when inheriting parent's message. */
  initialMessage?: string;
  /** Whether the initial message is already confirmed. */
  initiallyConfirmed?: boolean;
}

/**
 * Encapsulates the "change log required before viewing" gate pattern:
 * - Computes whether a changelog is required for the given permission
 * - Manages dialog open/close state and in-progress message
 * - Blocks the next action (`canFetch`) until the user confirms a message
 */
export function useChangeLogGate({
  permissionValue,
  changeLogRoles,
  initialOpen,
  initialMessage = '',
  initiallyConfirmed = false,
}: UseChangeLogGateOptions) {
  const needsChangeLog = isAbleToSeeUIElement(changeLogRoles, permissionValue);

  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [confirmedChangeLogMessage, setConfirmedChangeLogMessage] = useState(
    initiallyConfirmed ? initialMessage : ''
  );
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(
    initialOpen ?? (needsChangeLog && !initiallyConfirmed)
  );
  const [canFetch, setCanFetch] = useState(
    !needsChangeLog || initiallyConfirmed
  );

  /** Open the changelog dialog. */
  function openDialog() {
    setOpenChangeLogDialog(true);
  }

  /** Close the dialog without confirming (clears the in-progress message). */
  function dismiss() {
    setOpenChangeLogDialog(false);
    setChangeLogMessage('');
  }

  /**
   * Confirm the changelog message, unblock the query, and close the dialog.
   * Pass an optional `onConfirm` callback for page-specific side effects
   * (e.g. updating URL search params).
   */
  function confirm(onConfirm?: () => void) {
    setConfirmedChangeLogMessage(changeLogMessage);
    setOpenChangeLogDialog(false);
    setChangeLogMessage('');
    setCanFetch(true);
    onConfirm?.();
  }

  /**
   * Reset gate to its initial state — use when the user clears their search
   * or navigates away so the next search requires a fresh message.
   */
  function reset() {
    setCanFetch(!needsChangeLog);
    setConfirmedChangeLogMessage('');
  }

  return {
    needsChangeLog,
    canFetch,
    /** Exposed for cases where fetch must be toggled directly (e.g. Search button). */
    setCanFetch,
    confirmedChangeLogMessage,
    changeLogMessage,
    setChangeLogMessage,
    openChangeLogDialog,
    openDialog,
    confirm,
    dismiss,
    reset,
  };
}
